import { resumenVenta, type VerificacionVenta } from "@/lib/ventas";

/**
 * Aviso anti-goles: ¿los IMEIs capturados los vendimos nosotros?
 *
 * SIEMPRE informativo — nunca deshabilita ni bloquea nada: puede que el mes
 * en curso no esté cargado todavía, o que el equipo sea de una venta vieja
 * que el CRM no exportó. La decisión de dar soporte es humana.
 */
export default function AvisoVentasImeis({
  verificaciones,
}: {
  verificaciones: VerificacionVenta[];
}) {
  if (verificaciones.length === 0) return null;
  const sinVenta = verificaciones.filter((v) => !v.vendido);
  const conVenta = verificaciones.filter((v) => v.vendido);

  if (sinVenta.length === 0) {
    return (
      <p className="mt-2 text-xs text-emerald-400/90">
        {conVenta.length === 1
          ? `✓ ${resumenVenta(conVenta[0])}`
          : `✓ Los ${conVenta.length} equipos aparecen en nuestras ventas.`}
      </p>
    );
  }

  return (
    <div className="mt-2 space-y-1 rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-200">
      <p className="font-medium">
        ⚠️ {sinVenta.length === 1 ? "Este equipo no aparece" : `Estos ${sinVenta.length} equipos no aparecen`} en
        nuestras ventas:
      </p>
      <div className="flex flex-wrap gap-1.5">
        {sinVenta.map((v) => (
          <a
            key={v.valor}
            href={`/ventas?q=${encodeURIComponent(v.valor)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="font-mono underline decoration-dotted hover:text-amber-100"
          >
            {v.valor}
          </a>
        ))}
      </div>
      <p className="text-amber-300/80">
        Puede que no lo hayamos vendido nosotros — o que el mes aún no esté
        cargado en Ventas. Puedes continuar; solo verifícalo antes de dar
        soporte. Toca un IMEI para buscarlo en Ventas (se abre en otra pestaña).
      </p>
      {conVenta.length > 0 && (
        <p className="text-emerald-400/90">
          ✓ {conVenta.length === 1 ? "El otro sí aparece" : `Los otros ${conVenta.length} sí aparecen`} en ventas.
        </p>
      )}
    </div>
  );
}

/** Marca compacta por IMEI para la pestaña IMEIs: "✓ ventas" o "⚠ sin
 * venta", con link a Ventas en pestaña nueva. El detalle de la venta va a la
 * vista con `DetalleVenta` (antes vivía en el title: invisible en el celular). */
export function MarcaVenta({ check }: { check?: VerificacionVenta }) {
  if (!check) return null;
  return (
    <a
      href={`/ventas?q=${encodeURIComponent(check.valor)}`}
      target="_blank"
      rel="noopener noreferrer"
      className={`shrink-0 text-[10px] whitespace-nowrap underline decoration-dotted ${
        check.vendido
          ? "text-emerald-400/80 hover:text-emerald-300"
          : "text-amber-300/90 hover:text-amber-200"
      }`}
    >
      {check.vendido ? "✓ ventas" : "⚠ sin venta"}
    </a>
  );
}

/** A quién y cuándo se vendió, en letra chica. Solo si aparece en Ventas: el
 * «⚠ sin venta» de `MarcaVenta` ya dice lo otro. */
export function DetalleVenta({ check }: { check?: VerificacionVenta }) {
  if (!check?.vendido) return null;
  return <div className="text-[11px] text-zinc-500">{resumenVenta(check)}</div>;
}
